/**
 * Permalink Utilities
 * 
 * Functions for encoding/decoding application state to/from URLs
 * and generating shareable permalinks.
 */

/**
 * Encodes application state into a permalink URL
 * @param {Object} state - Application state object
 * @param {string} state.selectedNoteId - Currently selected note ID
 * @param {string} state.selectedNotebookId - Currently selected notebook ID
 * @param {string} state.selectedAreaOfFocusId - Currently selected area of focus ID
 * @param {string|string[]} state.activePanel - Currently active panel(s) (e.g., 'info', ['info', 'open-notes'])
 * @param {string} state.searchQuery - Current search query
 * @param {string} state.itemStatus - Item status ('notes' or 'trash')
 * @returns {string} Permalink URL path with query parameters
 */
export const encodePermalink = (state = {}) => {
  const {
    selectedNoteId,
    selectedNotebookId,
    selectedAreaOfFocusId,
    activePanel,
    searchQuery,
    itemStatus = 'notes',
  } = state;

  // Build base path
  let path = '/';

  // Determine base path based on context
  if (selectedNoteId) {
    path = `/e/${selectedNoteId}`;
  } else if (selectedNotebookId && !selectedNoteId) {
    path = `/notebooks/${selectedNotebookId}`;
  } else if (selectedAreaOfFocusId && !selectedNoteId) {
    path = `/aof/${selectedAreaOfFocusId}`;
  } else if (itemStatus === 'trash') {
    path = '/trash';
  }

  // Build query parameters
  const queryParams = new URLSearchParams();

  if (selectedNotebookId && selectedNoteId) {
    // If we have both note and notebook, add notebook as query param
    queryParams.append('notebook', selectedNotebookId);
  }

  if (selectedAreaOfFocusId && selectedNoteId) {
    // If we have both note and AOF, add AOF as query param
    queryParams.append('aof', selectedAreaOfFocusId);
  }

  // Handle multiple panels - use selectedpanels as comma-separated parameter
  if (activePanel) {
    const panels = Array.isArray(activePanel) ? activePanel : [activePanel];
    // Filter out null/undefined values and sort for consistency
    const validPanels = panels.filter(panel => panel).sort();
    if (validPanels.length > 0) {
      // Use selectedpanels as comma-separated value
      queryParams.append('selectedpanels', validPanels.join(','));
    }
  }

  if (searchQuery) {
    queryParams.append('search', searchQuery);
  }

  const queryString = queryParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};

/**
 * Decodes a URL into application state
 * @param {string} pathname - URL pathname (e.g., '/e/123')
 * @param {string} search - URL search string (e.g., '?panel=info&notebook=456')
 * @returns {Object} Decoded state object
 */
export const decodePermalink = (pathname, search = '') => {
  const state = {
    selectedNoteId: null,
    selectedNotebookId: null,
    selectedAreaOfFocusId: null,
    activePanel: [], // Changed to array to support multiple panels
    searchQuery: null,
    itemStatus: 'notes',
  };

  // Parse pathname
  const pathParts = pathname.split('/').filter(Boolean);

  if ((pathParts[0] === 'e' || pathParts[0] === 'notes') && pathParts[1]) {
    state.selectedNoteId = pathParts[1];
  } else if (pathParts[0] === 'notebooks' && pathParts[1]) {
    state.selectedNotebookId = pathParts[1];
  } else if (pathParts[0] === 'aof' && pathParts[1]) {
    state.selectedAreaOfFocusId = pathParts[1];
  } else if (pathParts[0] === 'areas-of-focus' && pathParts[1]) {
    state.selectedAreaOfFocusId = pathParts[1];
  } else if (pathParts[0] === 'trash') {
    state.itemStatus = 'trash';
  }

  // Parse query parameters
  if (search) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

    const notebookId = params.get('notebook');
    if (notebookId) {
      state.selectedNotebookId = notebookId;
    }

    const aofId = params.get('aof');
    if (aofId) {
      state.selectedAreaOfFocusId = aofId;
    }

    const noteId = params.get('note');
    if (noteId) {
      state.selectedNoteId = noteId;
    }

    // Get selectedpanels (comma-separated) - primary method
    const selectedpanels = params.get('selectedpanels');
    if (selectedpanels) {
      // Split comma-separated values and filter out empty strings
      state.activePanel = selectedpanels.split(',').map(p => p.trim()).filter(p => p);
    } else {
      // Fallback: support legacy 'panel' parameter for backwards compatibility
      const panels = params.getAll('panel');
      if (panels.length > 0) {
        state.activePanel = panels;
      }
    }

    const searchQuery = params.get('search');
    if (searchQuery) {
      state.searchQuery = searchQuery;
    }
  }

  return state;
};

/**
 * Gets the full permalink URL for the current application state
 * @param {Object} appState - Current XState app state context
 * @returns {string} Full URL including origin
 */
export const getPermalink = (appState) => {
  const state = {
    selectedNoteId: appState.selectedNoteId || null,
    selectedNotebookId: appState.selectedNotebookId || null,
    selectedAreaOfFocusId: appState.selectedAreaOfFocusId || null,
    activePanel: appState.activePanel || null,
    searchQuery: appState.searchQuery || null,
    itemStatus: appState.itemStatus || 'notes',
  };

  const path = encodePermalink(state);
  return typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;
};

/**
 * Copies a permalink to the clipboard
 * @param {string} url - URL to copy
 * @returns {Promise<void>}
 */
export const copyPermalinkToClipboard = async (url) => {
  if (typeof window === 'undefined' || !navigator.clipboard) {
    throw new Error('Clipboard API not available');
  }

  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackError) {
      document.body.removeChild(textArea);
      throw fallbackError;
    }
  }
};

/**
 * Gets permalink for a specific note
 * @param {string} noteId - Note ID
 * @param {Object} options - Additional options
 * @param {string} options.panel - Panel to show (e.g., 'info')
 * @param {string} options.notebookId - Notebook ID for context
 * @returns {string} Full permalink URL
 */
export const getNotePermalink = (noteId, options = {}) => {
  const { panel, notebookId } = options;
  let url = `/e/${noteId}`;
  const params = new URLSearchParams();

  if (panel) {
    params.append('panel', panel);
  }

  if (notebookId) {
    params.append('notebook', notebookId);
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
};

/**
 * Gets permalink for a specific notebook
 * @param {string} notebookId - Notebook ID
 * @param {Object} options - Additional options
 * @param {string} options.noteId - Note ID to select
 * @returns {string} Full permalink URL
 */
export const getNotebookPermalink = (notebookId, options = {}) => {
  const { noteId } = options;
  let url = `/notebooks/${notebookId}`;

  if (noteId) {
    url += `?note=${noteId}`;
  }

  return typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
};

/**
 * Gets permalink for a specific area of focus
 * @param {string} aofId - Area of focus ID
 * @param {Object} options - Additional options
 * @param {string} options.noteId - Note ID to select
 * @param {string} options.panel - Panel to show
 * @returns {string} Full permalink URL
 */
export const getAofPermalink = (aofId, options = {}) => {
  const { noteId, panel } = options;
  let url = `/aof/${aofId}`;
  const params = new URLSearchParams();

  if (noteId) {
    params.append('note', noteId);
  }

  if (panel) {
    params.append('panel', panel);
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
};

